/**
 * Small shared pieces of the Settings sub-view (plan docs/plans/lms-plan.md
 * section 13 tab 7, 4.10, 9.1; WP-48). Class strings are the design skill's
 * recipes (G13); the card, label and pill helpers come from WP-43's
 * components/pickers/shared.tsx so every Manage screen reads the same.
 */
import { format } from 'date-fns';
import { Pill } from '../../../components/pickers/shared';
import type { PillTone } from '../../../components/pickers/shared';

/** A card's heading row: the h2 (the tab's PageHeader holds the h1). */
export const cardTitleCls = 'text-sm font-semibold text-gray-900 dark:text-gray-100';

/** A field's hint line. */
export const hintCls = 'mt-1 text-xs text-gray-500 dark:text-gray-400';

/** A field's error line (red, with the words). */
export const fieldErrorCls = 'mt-1 text-xs font-medium text-red-700 dark:text-red-400';

/** "23 Sep 2026, 07:05" in the viewer's zone, or null for nothing or an unreadable value. */
export function formatInstant(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : format(d, 'd MMM yyyy, HH:mm');
}

const JOB_TONES: Record<string, { tone: PillTone; word: string }> = {
  done: { tone: 'green', word: 'Done' },
  failed: { tone: 'red', word: 'Failed' },
  running: { tone: 'amber', word: 'Running' },
};

/** A job row's status as a pill with its word (5.8 lms_jobs.status); null is "Never run". */
export function JobStatusPill({ status, testId }: { status: string | null | undefined; testId?: string }) {
  if (!status) {
    return (
      <Pill tone="gray" testId={testId}>
        Never run
      </Pill>
    );
  }
  const known = JOB_TONES[status];
  return (
    <Pill tone={known ? known.tone : 'gray'} testId={testId}>
      {known ? known.word : status}
    </Pill>
  );
}

/** "renewals_created" -> "Renewals created". */
export function humanizeKey(key: string): string {
  const words = key.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/** At most this many lines of a run summary are shown. */
export const SUMMARY_LINE_CAP = 12;

/**
 * A pass summary as short lines (services/lms/jobs/daily.js and weekly.js):
 * the steps that failed first (G10: a failed step is named, never cut off),
 * then each top-level count. Strings such as network ids are left out.
 */
export function summaryLines(summary: Record<string, unknown> | null | undefined): string[] {
  if (!summary || typeof summary !== 'object') return [];
  const lines: string[] = [];
  const errors = (summary as { errors?: unknown }).errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const steps = errors
      .map((e) => (e && typeof e === 'object' && typeof (e as { step?: unknown }).step === 'string' ? (e as { step: string }).step : null))
      .filter((s): s is string => Boolean(s));
    lines.push(`Steps that failed: ${steps.length > 0 ? steps.join(', ') : errors.length}`);
  }
  for (const [key, value] of Object.entries(summary)) {
    if (typeof value === 'number' && Number.isFinite(value)) lines.push(`${humanizeKey(key)}: ${value.toLocaleString()}`);
  }
  return lines.slice(0, SUMMARY_LINE_CAP);
}

export { Pill };
export type { PillTone };
